
import { useEffect, useState, useRef, useMemo, Suspense, useCallback } from "react"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  closestCorners,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
} from "@dnd-kit/core"
import { useNavigate } from "react-router"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { ALL_ROLES } from "@/lib/team/roles"
import { AppLayout } from "@/components/layout/app-layout"
import {
  SearchLg as Search,
  DownloadCloud01 as Download,
  ZapFast,
  Eye,
  List,
  LayoutAlt04 as Columns3,
  XClose,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  // HIDE_SCHED_INC: CalendarDate icon was used for the Schedule row action.
} from "@untitledui/icons"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates, useUpdateEstimateStatus } from "@/lib/api/use-estimates"
import { useTeamMembers } from "@/lib/api/use-teams"
import { estimatesApi, estimateAuthorShort, isDeferred } from "@/lib/api/estimates"
import type { ApiEstimate, EstimateStatus } from "@/lib/api/types"
import { vehicleDisplayName, estimateCustomerName } from "@/lib/api/types"
import { StatusBadge } from "@/components/estimates/status-badge"
import { DealershipFilter } from "@/components/estimates/dealership-filter"
import { DroppableColumn } from "@/components/estimates/droppable-column"
import { StartFollowUpDialog } from "@/components/estimates/start-follow-up-dialog"
import { SortPill } from "@/components/estimates/sort-pill"
import { DesignSelect } from "@/components/ui/design-select"
import { Tooltip } from "@/components/ui/design-tooltip"
import { getEstimateAge } from "@/lib/estimates/estimate-age"
import { toast } from "sonner"
import { PipelineCard } from "@/components/estimates/pipeline-card"
import { estimateVerdict, isCleanEstimate } from "@/lib/estimates/estimate-verdict"
// HIDE_SCHED_INC: BookAppointmentModal import removed — Scheduling is hidden.
import { motion, AnimatePresence } from "motion/react"
import { cx } from "@/lib/utils/cx"

// Pipeline columns rendered side-by-side in the Board view. Drafts are
// intentionally excluded — they're the creator's local working state and
// only surface via the list view's status filter. Loading is bucketed into
// Pending because it's a transient system state, not something an advisor
// manages separately. Each column declares the status(es) it accepts; the
// `key` doubles as the drop-target id, so dropping a card on the column
// transitions the estimate to that status (see handleDragEnd).
const PIPELINE_COLUMNS: {
  key: EstimateStatus
  label: string
  statuses: EstimateStatus[]
  accent: string
}[] = [
  { key: "Pending", label: "Pending", statuses: ["Pending", "Loading"], accent: "bg-amber-400" },
  { key: "Confirmed", label: "Confirmed", statuses: ["Confirmed"], accent: "bg-teal-400" },
  { key: "InProgress", label: "In Progress", statuses: ["InProgress"], accent: "bg-blue-400" },
  { key: "Scheduled", label: "Scheduled", statuses: ["Scheduled"], accent: "bg-sky-400" },
]

// Completed and Declined are outcomes, not stages — a car that's done is done.
// Left on the board permanently they pull the eye and squeeze the four lanes
// that still need work. They fold behind a counted "Closed" toggle instead,
// which is URL-persisted (?closed=1) so a manager's account-review view stays
// bookmarkable — the workflow they actually described was reviewing them ON
// the board, so hiding them in another view would answer the wrong question.
const CLOSED_COLUMNS: typeof PIPELINE_COLUMNS = [
  { key: "Completed", label: "Completed", statuses: ["Completed"], accent: "bg-emerald-500" },
  { key: "Declined", label: "Declined", statuses: ["Declined"], accent: "bg-red-400" },
]

// Scheduled and Completed genuinely arrive on their own — a customer booking
// and an RO closing are webhooks, not decisions. InProgress is NOT one of them
// any more: moving an estimate there is the human act of starting contact (it
// releases the drip and the booking link), so the shop must be able to do it
// deliberately — including straight from Pending, which is what they actually
// do when an estimate obviously needs a call.
const SYSTEM_DRIVEN_STATUSES = new Set<EstimateStatus>([
  "Scheduled",
  "Completed",
])

function EstimatesPageInner() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { activeTeamId, activeTeamKind } = useOrganization()
  const [searchQuery, setSearchQuery] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectedStatus, setSelectedStatus] = useSearchParamState("status", "all")
  // Who ran the estimate — filters list AND board (Anthony, Jul 28).
  const [authorId, setAuthorId] = useSearchParamState("author", "all")
  // Body shop only — narrow estimates to a chosen subset of connected
  // dealerships. Persisted as a comma-separated list in `?dealerships=` so
  // filtered views are bookmarkable + shareable. The string→array round trip
  // tolerates legacy/empty values.
  const [dealershipsParam, setDealershipsParam] = useSearchParamState("dealerships", "")
  const dealershipIds = dealershipsParam ? dealershipsParam.split(",").filter(Boolean) : []
  const setDealershipIds = (ids: string[]) => setDealershipsParam(ids.join(","))
  // With no ?layout param a user lands on the right view for their team; an
  // explicit toggle still writes/clears the param normally. Body shops get
  // the board (the day's follow-up work lives on /tasks, not here).
  const layoutDefault = activeTeamKind === "bodyshop" ? "pipeline" : "list"
  const [pipelineView, setPipelineView] = useSearchParamState("layout", layoutDefault) as [
    "list" | "pipeline",
    (v: string) => void,
  ]
  // Closed lanes are off the board by default; ?closed=1 brings them back for
  // an account review without sending the reviewer to a different screen.
  const [closedParam, setClosedParam] = useSearchParamState("closed", "0")
  const showClosed = closedParam === "1"
  const [sortField, setSortField] = useState<"date" | "customer" | "vehicle" | "amount">("date")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")
  // (Per-row "..." dropdown was removed when actions moved inline; no
  // outside-click handler needed anymore.)

  // Legacy ?view=<estimateId> deep link — redirected to /estimates/:id below
  // so old shareable URLs keep working. Once the redirect runs the param is
  // cleared from the URL on its way out.
  const [viewEstimateId, setViewEstimateId] = useSearchParamState("view", "")
  const [activeEstimateId, setActiveEstimateId] = useState<string | null>(null)
  // The estimate awaiting the "start follow-up" confirmation (drag or button).
  const [startFollowUp, setStartFollowUp] = useState<ApiEstimate | null>(null)

  // HIDE_SCHED_INC: schedule modal state removed — Scheduling is hidden.

  // Mouse drags after an 8px move; touch requires a short press-hold so a
  // finger-swipe scrolls the column instead of accidentally dragging a card
  // (F39 — mobile drag was fighting native scroll).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
  )

  const { data: allEstimates = [], isLoading: isLoadingEstimates } = useEstimates(activeTeamId)

  const totalEstimatesBeforeFilter = allEstimates.length

  // Pipeline drag-to-move is a body-shop action — moving an estimate between
  // statuses (e.g. Pending → InProgress, → Completed) is part of their work
  // queue. Dealerships see the same board but read-only; status writes for
  // them go through dedicated actions (send / cancel) elsewhere.
  const canMoveInPipeline = activeTeamKind === "bodyshop"
  // A dealership's own list repeats its name on every row — pure noise. Only
  // body shops (multi-store inbound) get the "From" column.
  const isBodyShopView = activeTeamKind === "bodyshop"


  const handleCreateEstimate = () => {
    navigate("/estimates/quick?return=/estimates")
  }

  const handleCreateFullEstimate = () => {
    navigate("/estimates/full?return=/estimates")
  }

  const statusMutation = useUpdateEstimateStatus()

  const handleStatusChange = (estimateId: string, newStatus: EstimateStatus) => {
    statusMutation.mutate(
      { estimateId, status: newStatus },
      {
        // Dropping into Confirmed is the same act as the Confirm button —
        // same words, so the drag never feels like a different feature (#48).
        onSuccess: () =>
          toast.success(
            newStatus === "Confirmed"
              ? "Confirmed — automated follow-up started"
              : `Estimate marked as ${newStatus}`,
          ),
        onError: () => toast.error('Failed to update estimate status'),
      },
    )
  }

  const estimates = useMemo(() => {
    if (selectedStatus === "all") return allEstimates
    return allEstimates.filter(est => est.status === selectedStatus)
  }, [allEstimates, selectedStatus])

  // Author options: everyone who actually wrote one of these estimates,
  // enriched with role + department (name · role · department is the user's
  // identity wherever an author is picked). Estimates cross the dealership ⇄
  // body-shop boundary, so the counterparty roster loads too — its id comes
  // from the estimates themselves (a body-shop user has no dealership in
  // their org context).
  const counterTeamId = useMemo(() => {
    const tid = activeTeamId != null ? Number(activeTeamId) : null
    for (const e of allEstimates) {
      const d = e.dealership?.id
      const b = e.bodyshop?.id
      if (d != null && d !== tid) return d
      if (b != null && b !== tid) return b
    }
    return null
  }, [allEstimates, activeTeamId])
  const { data: ownMembers = [] } = useTeamMembers(
    activeTeamId ? Number(activeTeamId) : null,
  )
  const { data: counterMembers = [] } = useTeamMembers(counterTeamId)
  const members = useMemo(
    () => [...ownMembers, ...counterMembers],
    [ownMembers, counterMembers],
  )
  const deptByUserId = useMemo(
    () => new Map(members.map((m) => [String(m.id), m.department])),
    [members],
  )
  const authorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const e of allEstimates) {
      const c = e.createdBy
      if (c?.id == null) continue
      const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || `User #${c.id}`
      seen.set(String(c.id), name)
    }
    const meta = new Map(members.map((m) => [String(m.id), m]))
    return [...seen.entries()]
      .map(([id, name]) => {
        const m = meta.get(id)
        return {
          id,
          name,
          sub: m ? `${m.role}${m.department ? ` · ${m.department}` : ""}` : undefined,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [allEstimates, members])
  // Departments that actually authored something — pickable as a filter too.
  const departmentOptions = useMemo(() => {
    const set = new Set<string>()
    for (const a of authorOptions) {
      const d = deptByUserId.get(a.id)
      if (d) set.add(d)
    }
    return [...set].sort()
  }, [authorOptions, deptByUserId])
  const selectedAuthorLabel =
    authorId === "all"
      ? null
      : authorId.startsWith("dept:")
        ? authorId.slice(5)
        : (authorOptions.find((a) => a.id === authorId)?.name ?? null)

  const filteredEstimates = estimates.filter((estimate) => {
    // Who ran it — a person or a whole department; applies to the list and
    // every board column alike.
    if (authorId !== "all") {
      const cid = String(estimate.createdBy?.id ?? "")
      if (authorId.startsWith("dept:")) {
        if (deptByUserId.get(cid) !== authorId.slice(5)) return false
      } else if (cid !== authorId) return false
    }
    // Dealership filter (body shops only — comes from URL). Empty selection
    // means "all dealerships." Estimates whose `dealership` is missing pass
    // through any active filter — we don't hide records for missing data.
    if (dealershipIds.length > 0) {
      const did = estimate.dealership?.id
      if (did == null || !dealershipIds.includes(String(did))) return false
    }
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const customerName = estimateCustomerName(estimate).toLowerCase()
    const vehicleName = vehicleDisplayName(estimate.vehicle).toLowerCase()
    const vin = (estimate.vehicle?.vin ?? '').toLowerCase()
    return (
      customerName.includes(query) ||
      vehicleName.includes(query) ||
      vin.includes(query) ||
      estimate.status.toLowerCase().includes(query)
    )
  })

  // Closed rows are scoped to the last 30 days: "the review" means this month,
  // and all-time would dump hundreds of cards onto the board.
  const closedCount = useMemo(() => {
    const cutoff = Date.now() - 30 * 86_400_000
    return filteredEstimates.filter(
      (e) =>
        (e.status === "Completed" || e.status === "Declined") &&
        new Date(e.updatedAt ?? e.createdAt).getTime() >= cutoff,
    ).length
  }, [filteredEstimates])

  const handleExportFiltered = () => {
    const csvData = filteredEstimates.map((est) => ({
      Date: new Date(est.createdAt).toLocaleDateString(),
      Customer: estimateCustomerName(est),
      VIN: est.vehicle?.vin ?? '',
      Vehicle: vehicleDisplayName(est.vehicle),
      Dealership: est.dealership?.name ?? '',
      Status: est.status,
      Min: est.min.toFixed(2),
      Max: est.max.toFixed(2),
      Avg: est.avg.toFixed(2),
    }))
    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers.map((header) => `"${String(row[header as keyof typeof row]).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `estimates-filtered-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(`Exported ${filteredEstimates.length} estimates to CSV`)
  }

  const hasActiveFilters =
    selectedStatus !== "all" ||
    searchQuery !== "" ||
    dealershipIds.length > 0 ||
    authorId !== "all"

  // Group estimates by status for the kanban. Drafts are dropped on the
  // floor — they're not surfaced in the board (see PIPELINE_COLUMNS comment).
  const estimatesByStatus = useMemo(() => {
    const grouped: Record<string, ApiEstimate[]> = {
      Draft: [], Loading: [], Pending: [], Confirmed: [], InProgress: [], Scheduled: [], Completed: [], Declined: [],
    }
    filteredEstimates.forEach((estimate) => {
      grouped[estimate.status].push(estimate)
    })
    return grouped
  }, [filteredEstimates])

  const sortedEstimates = [...filteredEstimates].sort((a, b) => {
    let comparison = 0
    switch (sortField) {
      case "date": comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(); break
      case "customer": comparison = estimateCustomerName(a).localeCompare(estimateCustomerName(b)); break
      case "vehicle": comparison = vehicleDisplayName(a.vehicle).localeCompare(vehicleDisplayName(b.vehicle)); break
      case "amount": comparison = a.avg - b.avg; break
    }
    return sortDirection === "asc" ? comparison : -comparison
  })

  // Headline stats — one dominant "attention" tile + a couple of secondary
  // numbers. Only Pending is the human's job now (Confirm); InProgress →
  // Scheduled → Completed run on autopilot, so they don't count as work here.
  // Anything aging more than 24h gets called out specifically.
  const pendingCount = filteredEstimates.filter(e => e.status === "Pending").length
  const attentionCount = pendingCount
  const agingCount = useMemo(
    () =>
      filteredEstimates.filter((e) => {
        const sev = getEstimateAge(e).severity
        return sev === "warm" || sev === "stale"
      }).length,
    [filteredEstimates],
  )
  const todayStr = new Date().toISOString().split("T")[0]
  const todayCount = filteredEstimates.filter(e => e.createdAt.startsWith(todayStr)).length
  const weekAgoMs = Date.now() - 7 * 86_400_000
  const completedThisWeek = filteredEstimates.filter(
    (e) => e.status === "Completed" && new Date(e.createdAt).getTime() >= weekAgoMs,
  ).length

  // (Header click-to-sort removed — SortPill handles sort field + direction
  // in one popover, which is more discoverable for non-power-users.)

  const handleDownloadPDF = async (estimateId: string) => {
    try {
      const { blob, filename } = await estimatesApi.exportPdf(estimateId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? `estimate-${estimateId}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download PDF')
    }
  }

  const handleViewDetails = useCallback(
    (estimateId: string) => navigate(`/estimates/${estimateId}`),
    [navigate],
  )

  // Legacy ?view=<id> deep link → forward to the new /estimates/:id route.
  // The detail page is the source of truth now; this just keeps old shared
  // URLs working.
  useEffect(() => {
    if (!viewEstimateId) return
    const id = viewEstimateId
    setViewEstimateId("")
    navigate(`/estimates/${id}`, { replace: true })
  }, [viewEstimateId, setViewEstimateId, navigate])

  const handleCall = (phone: string) => {
    toast.success(`Dialing ${phone}...`)
    window.location.href = `tel:${phone}`
  }

  const handleEmail = (email: string) => {
    toast.success(`Composing email to ${email}...`)
    window.location.href = `mailto:${email}`
  }

  const handleConfirm = (estimateId: string) => {
    statusMutation.mutate(
      { estimateId, status: "Confirmed" },
      {
        onSuccess: () => toast.success("Confirmed — automated follow-up started"),
        onError: () => toast.error("Couldn't confirm the estimate."),
      },
    )
  }

  // Batch-clear the "Looks good" pending estimates in one tap (the doctrine's
  // volume answer — a busy shop taps once, then eyeballs the flagged few).
  const handleBatchConfirm = (ids: string[]) => {
    if (ids.length === 0) return
    ids.forEach((id) => statusMutation.mutate({ estimateId: id, status: "Confirmed" }))
    toast.success(
      `Confirmed ${ids.length} clean estimate${ids.length === 1 ? "" : "s"} — follow-up started`,
    )
  }

  // The board's text button opens the customer's conversation (composer +
  // one-tap replies) instead of a dead end. Falls back to a toast only when
  // there's no customer to resolve.
  const handleSMS = (estimateId: string) => {
    const est = allEstimates.find((e) => String(e.id) === estimateId)
    const customerId = est?.vehicle?.customer?.id
    if (customerId != null) navigate(`/customers/${customerId}`)
    else toast("No customer on file for this estimate yet.")
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveEstimateId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!canMoveInPipeline || !over) { setActiveEstimateId(null); return }
    const estimateId = String(active.id)
    const newStatus = over.id as EstimateStatus
    // A booking and an RO close really do arrive on their own — reject drops
    // into those. InProgress is no longer one of them: it's the human's act.
    if (SYSTEM_DRIVEN_STATUSES.has(newStatus)) {
      toast("This one happens on its own once the customer books.")
      setActiveEstimateId(null)
      return
    }
    const estimateToUpdate = estimates.find((e) => String(e.id) === estimateId)
    setActiveEstimateId(null)
    if (!estimateToUpdate) {
      toast.error("Could not update estimate status")
      return
    }
    // Guard against same-status drops (drop on the column you came from).
    if (estimateToUpdate.status === newStatus) return

    // Moving INTO the working lane reaches a real customer — it releases the
    // drip and texts them their booking link. That must never be a silent
    // consequence of a drag, so it routes through the one dialog that names
    // what will happen (and says so honestly when the texts already went).
    if (newStatus === "InProgress") {
      setStartFollowUp(estimateToUpdate)
      return
    }
    // Moving BACK out is allowed, but don't pretend it un-sends anything.
    if (estimateToUpdate.status === "InProgress" && newStatus === "Confirmed") {
      handleStatusChange(estimateId, newStatus)
      if (estimateToUpdate.metadata?.marketingEnrolledAt) {
        toast("Moved back to Confirmed — the customer has already been texted.")
      }
      return
    }
    handleStatusChange(estimateId, newStatus)
  }

  // HIDE_SCHED_INC: handleSchedule removed — Scheduling is hidden. Restore
  // the function and the "schedule" entry in rowQuickActions when re-enabling.

  // Inline quick-actions on each row replace the previous "..." dropdown.
  // The two old menu items (View, Download) became surfaced icons.
  const rowQuickActions = (estimate: ApiEstimate) => [
    {
      key: "view",
      title: "View details",
      icon: Eye,
      onClick: () => handleViewDetails(String(estimate.id)),
    },
    {
      key: "download",
      title: "Download",
      icon: Download,
      onClick: () => handleDownloadPDF(String(estimate.id)),
    },
  ]

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="h-full flex flex-col">
          {/* Sticky Header */}
          <div className="border-b border-zinc-100 bg-white/80 backdrop-blur-xl sticky top-0 z-10 relative">
            {/* Top-right actions — Estimate (full wizard with line-item
                review) + Quick Estimate (service-line free-form capture). */}
            <div className="absolute right-4 md:right-6 top-4 md:top-6 flex items-center gap-2 z-20">
              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.05 }}
                onClick={handleCreateFullEstimate}
                className="h-10 px-3 md:px-4 rounded-xl bg-gradient-to-br from-zinc-900 to-black hover:from-zinc-800 hover:to-zinc-900 text-white text-sm font-semibold flex items-center gap-2 shadow-lg shadow-zinc-900/30 hover:shadow-zinc-900/40 active:scale-[0.99] transition-all duration-150 shrink-0"
                title="Full estimate — detailed walkthrough with line-item review"
              >
                <ZapFast className="size-4" />
                <span className="hidden md:inline">Estimate</span>
              </motion.button>
              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                onClick={handleCreateEstimate}
                className="h-10 px-3 md:px-4 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white text-sm font-semibold flex items-center gap-2 shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/35 active:scale-[0.99] transition-all duration-150 shrink-0"
              >
                <ZapFast className="size-4" />
                <span className="hidden md:inline">Quick Estimate</span>
                <span className="md:hidden">New</span>
              </motion.button>
            </div>

            <div className="max-w-[1400px] px-4 md:px-6 pt-4 md:pt-6 pb-3 md:pb-4 space-y-3 md:space-y-4">
              <div className="flex items-center justify-between gap-3">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">Estimates</h1>
                  <p className="text-sm text-zinc-400 mt-0.5 md:mt-1 hidden md:block">Manage and track all your collision estimates</p>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.05 }}
                className="flex flex-wrap items-stretch gap-2.5 md:gap-3"
              >
                {/* Attention tile — dominant stat. Click filters list to
                    Pending so the next gesture is the obvious one. */}
                <button
                  type="button"
                  onClick={() => setSelectedStatus(attentionCount > 0 ? "Pending" : "all")}
                  className={cx(
                    "group flex-1 min-w-[260px] flex items-center gap-3.5 px-4 py-3 rounded-2xl text-left transition-all duration-150 active:scale-[0.99]",
                    attentionCount > 0
                      ? "bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200/80 hover:border-amber-300 hover:shadow-md hover:shadow-amber-600/10"
                      : "bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200/80 hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-600/10",
                  )}
                >
                  <div
                    className={cx(
                      "size-11 rounded-xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
                      attentionCount > 0
                        ? "from-amber-500 to-amber-700 shadow-amber-600/25"
                        : "from-emerald-500 to-emerald-700 shadow-emerald-600/25",
                    )}
                  >
                    {attentionCount > 0 ? (
                      <AlertTriangle className="size-5 text-white" aria-hidden />
                    ) : (
                      <CheckCircle className="size-5 text-white" aria-hidden />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {attentionCount > 0 ? (
                      <>
                        {/* A dealership can't confirm — telling them it needs
                            THEIR attention is a lie. Name the real owner. */}
                        <p className="text-[15px] md:text-base font-bold text-amber-950 leading-tight">
                          <span className="tabular-nums">{attentionCount}</span>{" "}
                          {isBodyShopView
                            ? `${attentionCount === 1 ? "estimate needs" : "estimates need"} your attention`
                            : `${attentionCount === 1 ? "estimate is" : "estimates are"} waiting on the body shop`}
                        </p>
                        <p className="text-[12px] text-amber-900/75 mt-0.5">
                          {agingCount > 0
                            ? `${agingCount} aging more than 24h`
                            : "All recent — keep it moving"}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[15px] md:text-base font-bold text-emerald-950 leading-tight">
                          You're all caught up
                        </p>
                        <p className="text-[12px] text-emerald-900/75 mt-0.5">
                          Nothing pending right now.
                        </p>
                      </>
                    )}
                  </div>
                  <ArrowRight
                    className={cx(
                      "size-4 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5",
                      attentionCount > 0 ? "text-amber-700" : "text-emerald-700",
                    )}
                    aria-hidden
                  />
                </button>

                {/* Secondary stats — quieter on purpose. */}
                <div className="flex gap-2.5 md:gap-3 shrink-0">
                  <div className="flex flex-col justify-center px-4 py-3 rounded-2xl border border-zinc-200/60 bg-white min-w-[88px]">
                    <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide">
                      Today
                    </span>
                    <span className="text-xl font-bold text-zinc-900 tabular-nums leading-tight">
                      {todayCount}
                    </span>
                  </div>
                  <div className="flex flex-col justify-center px-4 py-3 rounded-2xl border border-zinc-200/60 bg-white min-w-[88px]">
                    <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide">
                      Done · 7d
                    </span>
                    <span className="text-xl font-bold text-zinc-900 tabular-nums leading-tight">
                      {completedThisWeek}
                    </span>
                  </div>
                </div>
              </motion.div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 md:gap-3">
                <div className="relative w-full sm:flex-1 sm:max-w-lg">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search customer, vehicle, VIN..."
                    value={searchInput}
                    onChange={(e) => {
                      const val = e.target.value
                      setSearchInput(val)
                      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
                      searchTimeoutRef.current = setTimeout(() => setSearchQuery(val), 300)
                    }}
                    className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                  />
                </div>
                <div className="flex gap-2 overflow-x-auto scrollbar-hide w-full sm:w-auto">
                  {activeTeamKind === "bodyshop" && (
                    <DealershipFilter
                      selectedIds={dealershipIds}
                      onChange={setDealershipIds}
                    />
                  )}
                  <DesignSelect.Root value={selectedStatus} onValueChange={setSelectedStatus}>
                    <DesignSelect.Trigger>
                      <DesignSelect.Value placeholder="All statuses" />
                    </DesignSelect.Trigger>
                    <DesignSelect.Content>
                      <DesignSelect.Item value="all">All statuses</DesignSelect.Item>
                      <DesignSelect.Item value="Draft">Draft</DesignSelect.Item>
                      <DesignSelect.Item value="Loading">Loading</DesignSelect.Item>
                      <DesignSelect.Item value="Pending">Pending</DesignSelect.Item>
                      <DesignSelect.Item value="Confirmed">Confirmed</DesignSelect.Item>
                      <DesignSelect.Item value="InProgress">In Progress</DesignSelect.Item>
                      <DesignSelect.Item value="Scheduled">Scheduled</DesignSelect.Item>
                      <DesignSelect.Item value="Completed">Completed</DesignSelect.Item>
                      <DesignSelect.Item value="Declined">Declined</DesignSelect.Item>
                    </DesignSelect.Content>
                  </DesignSelect.Root>
                  <DesignSelect.Root value={authorId} onValueChange={setAuthorId}>
                    <DesignSelect.Trigger>
                      <DesignSelect.Value placeholder="Everyone" />
                    </DesignSelect.Trigger>
                    <DesignSelect.Content>
                      <DesignSelect.Item value="all">Everyone</DesignSelect.Item>
                      {departmentOptions.length > 0 && (
                        <p className="px-2.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                          Departments
                        </p>
                      )}
                      {departmentOptions.map((d) => (
                        <DesignSelect.Item key={`dept:${d}`} value={`dept:${d}`}>
                          {d}
                        </DesignSelect.Item>
                      ))}
                      <p className="px-2.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                        People
                      </p>
                      {authorOptions.map((a) => (
                        <DesignSelect.Item key={a.id} value={a.id} sub={a.sub}>
                          {a.name}
                        </DesignSelect.Item>
                      ))}
                    </DesignSelect.Content>
                  </DesignSelect.Root>
                </div>
              </div>

              {/* Action row — content controls anchored close to the
                  list/board they affect. Left: List/Board toggle, Sort,
                  Export. Right: filter summary + clear. */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center bg-zinc-100 rounded-xl p-1">
                    {([
                      { id: "list" as const, icon: List, label: "List" },
                      { id: "pipeline" as const, icon: Columns3, label: "Board" },
                    ] as const).map((view) => (
                      <button
                        key={view.id}
                        onClick={() => setPipelineView(view.id)}
                        className={cx(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold transition-all duration-150",
                          pipelineView === view.id
                            ? "bg-white text-zinc-900 shadow-sm"
                            : "text-zinc-500 hover:text-zinc-900",
                        )}
                      >
                        <view.icon className="size-4" />
                        {view.label}
                      </button>
                    ))}
                  </div>
                  {pipelineView === "list" && (
                    <SortPill
                      field={sortField}
                      direction={sortDirection}
                      onChange={(f, d) => {
                        setSortField(f)
                        setSortDirection(d)
                      }}
                    />
                  )}
                  {/* Completed and Declined live behind this. A car that's
                      done is an outcome, not a stage — but an account review
                      still happens ON the board, so it's a toggle here rather
                      than a link to somewhere else. URL-persisted, so the
                      review view is bookmarkable. */}
                  {pipelineView === "pipeline" && closedCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setClosedParam(showClosed ? "0" : "1")}
                      className={cx(
                        "h-9 px-3 rounded-lg text-[12.5px] font-semibold transition-colors duration-150 inline-flex items-center gap-1.5",
                        showClosed
                          ? "bg-zinc-900 text-white"
                          : "bg-white text-zinc-600 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-50",
                      )}
                    >
                      Closed
                      <span
                        className={cx(
                          "tabular-nums",
                          showClosed ? "text-white/60" : "text-zinc-400",
                        )}
                      >
                        {closedCount}
                      </span>
                    </button>
                  )}
                  <Tooltip label="Export visible estimates as CSV">
                    <button
                      type="button"
                      onClick={handleExportFiltered}
                      className="size-10 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center hover:bg-white hover:border-zinc-300 transition-all duration-150 shrink-0"
                      aria-label="Export CSV"
                    >
                      <Download className="size-4 text-zinc-500" />
                    </button>
                  </Tooltip>
                </div>

                <div className="flex items-center gap-2 flex-wrap min-w-0 ml-auto">
                  <span className="text-[12.5px] text-zinc-400">
                    Showing{" "}
                    <span className="font-semibold text-zinc-700 tabular-nums">
                      {filteredEstimates.length}
                    </span>{" "}
                    of {totalEstimatesBeforeFilter}
                  </span>
                  {hasActiveFilters && (
                    <>
                      <span className="text-zinc-200">·</span>
                      {searchQuery && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-100 text-[11.5px] font-medium text-zinc-600">
                          &ldquo;{searchQuery}&rdquo;
                          <button onClick={() => { setSearchQuery(""); setSearchInput("") }} className="hover:text-zinc-900 transition-colors"><XClose className="size-3" /></button>
                        </span>
                      )}
                      {selectedStatus !== "all" && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-100 text-[11.5px] font-medium text-zinc-600">
                          Status: {selectedStatus}
                          <button onClick={() => setSelectedStatus("all")} className="hover:text-zinc-900 transition-colors"><XClose className="size-3" /></button>
                        </span>
                      )}
                      {dealershipIds.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-50 ring-1 ring-inset ring-sky-200 text-[11.5px] font-medium text-sky-800">
                          {dealershipIds.length === 1
                            ? "1 dealership"
                            : `${dealershipIds.length} dealerships`}
                          <button onClick={() => setDealershipIds([])} className="hover:text-sky-950 transition-colors"><XClose className="size-3" /></button>
                        </span>
                      )}
                      {selectedAuthorLabel && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-100 text-[11.5px] font-medium text-zinc-600">
                          {authorId.startsWith("dept:") ? "Dept" : "By"}: {selectedAuthorLabel}
                          <button onClick={() => setAuthorId("all")} className="hover:text-zinc-900 transition-colors"><XClose className="size-3" /></button>
                        </span>
                      )}
                      <button onClick={() => { setSelectedStatus("all"); setSearchQuery(""); setSearchInput(""); setDealershipIds([]); setAuthorId("all") }} className="text-zinc-500 hover:text-zinc-900 text-[11.5px] font-semibold transition-colors">
                        Clear all
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            <div className="flex-1 overflow-auto p-4 md:p-6">
              {/* Zero-estimates-ever empty state — distinct from
                  filtered-to-zero. Replaces both list and pipeline so the
                  user gets a clear "do this first" signal instead of an
                  empty table or empty Kanban. */}
              {!isLoadingEstimates && totalEstimatesBeforeFilter === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="max-w-xl mx-auto mt-8 md:mt-16 rounded-3xl border border-zinc-200/70 bg-white shadow-xl shadow-zinc-900/[0.04] p-8 md:p-10 text-center"
                >
                  <div className="size-16 rounded-2xl mx-auto bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shadow-lg shadow-emerald-600/25 mb-5">
                    <ZapFast className="size-8 text-white" strokeWidth={2} aria-hidden />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-zinc-900 tracking-tight">
                    Start your first estimate
                  </h2>
                  <p className="text-[14px] text-zinc-500 mt-2 leading-relaxed max-w-md mx-auto">
                    Snap a photo, and we'll generate a draft estimate in about 60 seconds.
                    You can edit, schedule, and send from there.
                  </p>
                  <button
                    type="button"
                    onClick={handleCreateEstimate}
                    className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40 active:scale-[0.99] transition-all duration-200"
                  >
                    <ZapFast className="size-[18px]" aria-hidden />
                    Quick Estimate
                  </button>
                  <p className="text-[11.5px] text-zinc-400 mt-4">
                    You can also import existing estimates anytime from Settings.
                  </p>
                </motion.div>
              ) : pipelineView === "list" ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  {/* Desktop table — one visual signal per concern: aging lives
                      with the date (escalating text, not a pill), money gets its
                      own bold column, the verdict is folded into the action
                      (Confirm vs Review), total-loss keeps the red rail + one
                      compact tag, and the "From" store column only exists for
                      body shops (a dealership's own list would repeat its name
                      on every row). */}
                  <div className="hidden md:block rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-100 bg-zinc-50/60">
                            {/* Plain labels — sort lives in the SortPill now,
                                so headers don't double as hidden controls. */}
                            {(
                              [
                                { key: "date", label: "Date", width: "w-[96px]" },
                                { key: "customer", label: "Customer" },
                                { key: "vehicle", label: "Vehicle" },
                                ...(isBodyShopView
                                  ? [{ key: "from", label: "From" }]
                                  : []),
                                { key: "amount", label: "Amount", width: "w-[110px]", right: true },
                                { key: "status", label: "Status", width: "w-[180px]" },
                                { key: "actions", label: "", width: "w-[190px]" },
                              ] as { key: string; label: string; width?: string; right?: boolean }[]
                            ).map((col) => (
                              <th
                                key={col.key}
                                className={`px-4 py-3 text-[10.5px] font-bold text-zinc-500 uppercase tracking-[0.08em] ${col.right ? "text-right" : "text-left"} ${col.width || ""}`}
                              >
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {sortedEstimates.length === 0 ? (
                            <tr>
                              <td colSpan={isBodyShopView ? 7 : 6} className="text-center py-16 text-sm text-zinc-400">
                                {hasActiveFilters
                                  ? "No estimates match these filters"
                                  : "No estimates yet — create your first one to get started."}
                              </td>
                            </tr>
                          ) : (
                            sortedEstimates.map((estimate) => {
                              const customerName = estimateCustomerName(estimate)
                              const vehicleName = vehicleDisplayName(estimate.vehicle)
                              const age = getEstimateAge(estimate)
                              const author = estimateAuthorShort(estimate)
                              const verdict =
                                estimate.status === "Pending" ? estimateVerdict(estimate) : null
                              const initials = customerName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                              const STATUS_BORDER: Record<string, string> = {
                                Draft: "border-l-zinc-300",
                                Loading: "border-l-orange-400",
                                Pending: "border-l-amber-400",
                                Confirmed: "border-l-teal-400",
                                InProgress: "border-l-blue-400",
                                Scheduled: "border-l-sky-400",
                                Completed: "border-l-emerald-500",
                                Declined: "border-l-red-400",
                              }
                              // Total-loss takes precedence over status for
                              // the left rail — it's the only signal a body
                              // shop never wants to miss while scanning.
                              const railClass = estimate.totalLoss
                                ? "border-l-red-500"
                                : STATUS_BORDER[estimate.status] ?? "border-l-transparent"
                              return (
                                <tr
                                  key={estimate.id}
                                  className={`group cursor-pointer hover:bg-zinc-50/60 transition-colors duration-100 border-l-[3px] ${railClass}`}
                                  onClick={() => handleViewDetails(String(estimate.id))}
                                >
                                  <td className="px-4 py-3.5 whitespace-nowrap align-middle">
                                    <p className="text-[12.5px] text-zinc-700 tabular-nums leading-tight">
                                      {new Date(estimate.createdAt).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </p>
                                    {/* Aging folded into the date — where time
                                        belongs — as escalating text, not a pill. */}
                                    {age.severity !== "none" && (
                                      <p
                                        className={cx(
                                          "text-[11px] tabular-nums leading-tight mt-0.5",
                                          age.severity === "stale"
                                            ? "text-red-600 font-medium"
                                            : age.severity === "warm"
                                              ? "text-amber-600 font-medium"
                                              : "text-zinc-400",
                                        )}
                                      >
                                        {age.label}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-4 py-3.5 align-middle">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="size-9 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                                        <span className="text-[11px] font-bold text-zinc-600 uppercase">
                                          {initials || "??"}
                                        </span>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-[13.5px] font-semibold text-zinc-900 truncate leading-tight">
                                          {customerName}
                                        </p>
                                        {author && (
                                          <p className="text-[11.5px] text-zinc-400 truncate leading-tight mt-0.5">
                                            by {author}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5 text-[12.5px] text-zinc-700 truncate max-w-[220px] align-middle">
                                    {vehicleName}
                                  </td>
                                  {isBodyShopView && (
                                    <td className="px-4 py-3.5 text-[12.5px] text-zinc-500 truncate max-w-[160px] align-middle">
                                      {estimate.dealership?.name ?? "—"}
                                    </td>
                                  )}
                                  <td className="px-4 py-3.5 text-right whitespace-nowrap align-middle">
                                    <span className="text-[13.5px] font-bold text-zinc-900 tabular-nums">
                                      ${estimate.avg.toLocaleString()}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3.5 align-middle">
                                    <div className="flex items-center gap-1.5">
                                      <StatusBadge status={estimate.status} />
                                      {estimate.totalLoss && (
                                        <span className="inline-flex items-center rounded-md bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-wide shrink-0">
                                          Total loss
                                        </span>
                                      )}
                                    </div>
                                    {/* One quiet context line, never a second
                                        pill: deferral or the ⚠ verdict reason. */}
                                    {(isDeferred(estimate) || verdict?.level === "review") && (
                                      <p
                                        className="text-[11px] text-amber-700 truncate leading-tight mt-1 max-w-[170px]"
                                        title={verdict?.reasons.join(" · ")}
                                      >
                                        {isDeferred(estimate)
                                          ? "Deferred"
                                          : `⚠ ${verdict!.reasons[0]}`}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex items-center justify-end gap-1.5">
                                      {/* The verdict IS the action: clean →
                                          one-tap Confirm; flagged → Review
                                          (opens the estimate so the ⚠ reasons
                                          get looked at, per the doctrine). */}
                                      {estimate.status === "Pending" &&
                                        canMoveInPipeline &&
                                        (verdict?.level === "review" ? (
                                          <button
                                            type="button"
                                            aria-label={`Review estimate for ${customerName}`}
                                            title={verdict.reasons.join(" · ")}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleViewDetails(String(estimate.id))
                                            }}
                                            className="h-8 px-2.5 rounded-lg bg-amber-500 text-white text-[11.5px] font-semibold hover:bg-amber-600 transition-colors inline-flex items-center gap-1 shrink-0"
                                          >
                                            <AlertTriangle className="size-3.5" aria-hidden /> Review
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            aria-label={`Confirm estimate for ${customerName}`}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleConfirm(String(estimate.id))
                                            }}
                                            className="h-8 px-2.5 rounded-lg bg-teal-600 text-white text-[11.5px] font-semibold hover:bg-teal-700 transition-colors inline-flex items-center gap-1 shrink-0"
                                          >
                                            <CheckCircle className="size-3.5" aria-hidden /> Confirm
                                          </button>
                                        ))}
                                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-100">
                                        {rowQuickActions(estimate).map((a) => (
                                          <Tooltip key={a.key} label={a.title}>
                                            <button
                                              type="button"
                                              aria-label={a.title}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                a.onClick()
                                              }}
                                              className="size-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors duration-100"
                                            >
                                              <a.icon className="size-4" aria-hidden />
                                            </button>
                                          </Tooltip>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile card list — same data, no horizontal scroll. */}
                  <div className="md:hidden space-y-2.5">
                    {sortedEstimates.length === 0 ? (
                      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm py-14 text-center text-sm text-zinc-400">
                        {hasActiveFilters
                          ? "No estimates match these filters"
                          : "No estimates yet."}
                      </div>
                    ) : (
                      sortedEstimates.map((estimate) => {
                        const customerName = estimateCustomerName(estimate)
                        const vehicleName = vehicleDisplayName(estimate.vehicle)
                        const age = getEstimateAge(estimate)
                        const author = estimateAuthorShort(estimate)
                        const verdict =
                          estimate.status === "Pending" ? estimateVerdict(estimate) : null
                        const initials = customerName
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                        return (
                          // A div (not a button): the card carries a real
                          // Confirm/Review button inside — nesting buttons is
                          // invalid and breaks keyboard/AT. Tap-anywhere stays.
                          <div
                            key={estimate.id}
                            onClick={() => handleViewDetails(String(estimate.id))}
                            className={cx(
                              "w-full text-left rounded-2xl border bg-white shadow-sm overflow-hidden transition-all duration-100 active:scale-[0.99] cursor-pointer",
                              estimate.totalLoss
                                ? "border-red-300 ring-1 ring-red-100"
                                : "border-zinc-200/60",
                            )}
                          >
                            <div className="p-3.5 space-y-2.5">
                              <div className="flex items-center gap-3">
                                <div className="size-10 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                                  <span className="text-[12px] font-bold text-zinc-600 uppercase">
                                    {initials || "??"}
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[14px] font-semibold text-zinc-900 truncate leading-tight">
                                    {customerName}
                                  </p>
                                  <p className="text-[12px] text-zinc-500 truncate leading-tight mt-0.5">
                                    {vehicleName}
                                    {author && (
                                      <span className="text-zinc-400"> · by {author}</span>
                                    )}
                                  </p>
                                </div>
                                <p className="text-[15px] font-bold text-zinc-900 tabular-nums shrink-0">
                                  ${estimate.avg.toLocaleString()}
                                </p>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <StatusBadge status={estimate.status} />
                                  {estimate.totalLoss && (
                                    <span className="inline-flex items-center rounded-md bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-wide shrink-0">
                                      Total loss
                                    </span>
                                  )}
                                  {age.severity !== "none" && (
                                    <span
                                      className={cx(
                                        "text-[11px] tabular-nums shrink-0",
                                        age.severity === "stale"
                                          ? "text-red-600 font-medium"
                                          : age.severity === "warm"
                                            ? "text-amber-600 font-medium"
                                            : "text-zinc-400",
                                      )}
                                    >
                                      {age.label}
                                    </span>
                                  )}
                                  {isDeferred(estimate) && (
                                    <span className="text-[11px] text-amber-700 shrink-0">
                                      Deferred
                                    </span>
                                  )}
                                </div>
                                {isBodyShopView && estimate.dealership?.name && (
                                  <span className="text-[11px] text-zinc-400 truncate max-w-[130px]">
                                    {estimate.dealership.name}
                                  </span>
                                )}
                              </div>
                              {/* The one human action, thumb-sized — clean
                                  confirms in place, flagged opens to review. */}
                              {estimate.status === "Pending" && canMoveInPipeline && (
                                verdict?.level === "review" ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleViewDetails(String(estimate.id))
                                    }}
                                    className="w-full h-11 rounded-xl bg-amber-500 text-white text-[13px] font-semibold hover:bg-amber-600 transition-colors inline-flex items-center justify-center gap-1.5"
                                  >
                                    <AlertTriangle className="size-4" aria-hidden />
                                    Review — {verdict.reasons[0]}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleConfirm(String(estimate.id))
                                    }}
                                    className="w-full h-11 rounded-xl bg-teal-600 text-white text-[13px] font-semibold hover:bg-teal-700 transition-colors inline-flex items-center justify-center gap-1.5"
                                  >
                                    <CheckCircle className="size-4" aria-hidden />
                                    Confirm estimate
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </motion.div>
              ) : (
                // Multi-column kanban — all status columns visible at once,
                // horizontally scrollable. Each column is its own drop target
                // (id = column key = EstimateStatus); dropping a card onto a
                // different column transitions the estimate. The negative
                // margin + matching padding lets the scroll area bleed to the
                // page edges so the rightmost column fully reveals on swipe
                // without being clipped by the content container's padding.
                <div className="-mx-4 md:-mx-6 px-4 md:px-6 overflow-x-auto pb-2 snap-x snap-mandatory">
                  <div className="flex gap-3 md:gap-4 min-w-max">
                    {[...PIPELINE_COLUMNS, ...(showClosed ? CLOSED_COLUMNS : [])].map((col) => {
                      const isClosedLane = col.key === "Completed" || col.key === "Declined"
                      const rawItems = col.statuses
                        .flatMap((s) => estimatesByStatus[s] ?? [])
                        // Closed lanes show the review window, not all time.
                        .filter(
                          (e) =>
                            !isClosedLane ||
                            new Date(e.updatedAt ?? e.createdAt).getTime() >=
                              Date.now() - 30 * 86_400_000,
                        )
                      // Pending is the human's queue: ⚠️ "worth a look" first,
                      // then oldest (most urgent) first so nothing ages out at
                      // the bottom.
                      const items =
                        col.key === "Pending"
                          ? [...rawItems].sort((a, b) => {
                              const av = estimateVerdict(a).level === "review" ? 0 : 1
                              const bv = estimateVerdict(b).level === "review" ? 0 : 1
                              if (av !== bv) return av - bv
                              return (
                                new Date(a.createdAt).getTime() -
                                new Date(b.createdAt).getTime()
                              )
                            })
                          : rawItems
                      const cleanIds =
                        col.key === "Pending" && canMoveInPipeline
                          ? items
                              .filter((e) => isCleanEstimate(e))
                              .map((e) => String(e.id))
                          : []
                      return (
                        <div
                          key={col.key}
                          className="snap-start w-[85vw] min-w-[85vw] sm:w-[300px] sm:min-w-[300px] md:w-[320px] md:min-w-[320px]"
                        >
                          <DroppableColumn
                            id={col.key}
                            title={col.label}
                            eyebrow={
                              // In Progress stopped being automatic — moving a
                              // car there is now the human's decision to start
                              // contacting the customer.
                              col.key === "Scheduled" || col.key === "Completed"
                                ? "Automatic"
                                : undefined
                            }
                            count={items.length}
                            accentClassName={col.accent}
                            estimates={items}
                            canMove={canMoveInPipeline}
                            onViewDetails={handleViewDetails}
                            onCall={handleCall}
                            onEmail={handleEmail}
                            onSMS={handleSMS}
                            onConfirm={canMoveInPipeline ? handleConfirm : undefined}
                            headerAction={
                              cleanIds.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => handleBatchConfirm(cleanIds)}
                                  className="inline-flex items-center gap-1 h-6 px-2 rounded-lg bg-teal-600 text-white text-[11px] font-semibold hover:bg-teal-700 transition-colors"
                                >
                                  Approve {cleanIds.length} clean
                                </button>
                              ) : undefined
                            }
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            <DragOverlay dropAnimation={null} style={{ zIndex: 9999 }}>
              {activeEstimateId && estimates.find((e) => String(e.id) === activeEstimateId) ? (
                <div style={{ width: 300 }}>
                  <PipelineCard estimate={estimates.find((e) => String(e.id) === activeEstimateId)!} onViewDetails={handleViewDetails} onCall={handleCall} onEmail={handleEmail} onSMS={handleSMS} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Starting follow-up is the one act that reaches a real customer, so
            every path into InProgress — drag, card button, queue — lands here. */}
        {startFollowUp && (
          <StartFollowUpDialog
            open
            onClose={() => setStartFollowUp(null)}
            estimateId={startFollowUp.id}
            customerName={estimateCustomerName(startFollowUp)}
            hasPhone={!!startFollowUp.vehicle?.customer?.phone}
            alreadyContactedAt={
              typeof startFollowUp.metadata?.marketingEnrolledAt === "string"
                ? startFollowUp.metadata.marketingEnrolledAt
                : null
            }
            onStarted={() => setStartFollowUp(null)}
          />
        )}

        {/* HIDE_SCHED_INC: <BookAppointmentModal /> removed — Scheduling is hidden. */}
      </AppLayout>
    </ProtectedRoute>
  )
}

export default function EstimatesPage() {
  return (
    <Suspense>
      <EstimatesPageInner />
    </Suspense>
  )
}
